import { ref, computed, readonly } from 'vue'
import api from '@/api'

// ─── Types ───────────────────────────────────────────────────────────────────
interface FeatureGates {
  features: Record<string, boolean>
  actions: Record<string, boolean>
}

interface PlanInfo {
  name: string
  code: string
}

interface FeatureLimitation {
  current_plan: PlanInfo
  required_upgrade: PlanInfo
  message: string
}

// ─── Feature → minimum plan mapping (client-side, mirrors backend) ───────────
const FEATURE_PLAN_MAP: Record<string, { plan: string; label: string }> = {
  // Storefront Starter features
  products:              { plan: 'storefront_starter', label: 'Storefront Starter' },
  orders:                { plan: 'storefront_starter', label: 'Storefront Starter' },
  storefront:            { plan: 'storefront_starter', label: 'Storefront Starter' },
  storefront_branding:   { plan: 'module_commerce', label: 'Online Commerce module' },
  storefront_checkout:   { plan: 'module_commerce', label: 'Online Commerce module' },
  storefront_orders:     { plan: 'module_commerce', label: 'Online Commerce module' },
  storefront_invoices:   { plan: 'module_commerce', label: 'Online Commerce module' },
  customer_portal:       { plan: 'module_commerce', label: 'Online Commerce module' },

  // Anza features
  basic_pos:        { plan: 'anza', label: 'Anza' },
  pos:              { plan: 'anza', label: 'Anza' },
  inventory:        { plan: 'anza', label: 'Anza' },
  stock_adjustments:{ plan: 'anza', label: 'Anza' },

  // Kua Basic features
  mpesa:                { plan: 'module_payments', label: 'M-Pesa Payments module' },
  mpesa_manual:         { plan: 'free', label: 'Venmas Free' },
  mpesa_stk_push:       { plan: 'module_payments', label: 'M-Pesa Payments module' },
  mpesa_reconciliation: { plan: 'module_payments', label: 'M-Pesa Payments module' },
  etims:                { plan: 'module_etims', label: 'eTIMS Compliance module' },
  tax_compliance:       { plan: 'module_etims', label: 'eTIMS Compliance module' },
  delivery:             { plan: 'module_delivery', label: 'Delivery Operations module' },
  delivery_orders:      { plan: 'module_delivery', label: 'Delivery Operations module' },
  financial_reports:    { plan: 'kua_basic', label: 'Kua Basic' },
  supplier_management:  { plan: 'kua_basic', label: 'Kua Basic' },
  multi_user:           { plan: 'kua_basic', label: 'Kua Basic' },

  // Focused add-on modules
  advanced_analytics:       { plan: 'addon_analytics', label: 'Advanced Analytics module' },
  sales_forecasting:        { plan: 'addon_analytics', label: 'Advanced Analytics module' },
  profit_analysis:          { plan: 'addon_analytics', label: 'Advanced Analytics module' },
  customer_insights:        { plan: 'addon_analytics', label: 'Advanced Analytics module' },
  multi_location:           { plan: 'addon_multilocation', label: 'Multi-Location module' },
  centralized_inventory:    { plan: 'addon_multilocation', label: 'Multi-Location module' },
  cross_location_reporting: { plan: 'addon_multilocation', label: 'Multi-Location module' },
  location_analytics:       { plan: 'addon_multilocation', label: 'Multi-Location module' },
  email_marketing:          { plan: 'addon_notifications', label: 'Advanced Notifications module' },
  sms_notifications:        { plan: 'addon_notifications', label: 'Advanced Notifications module' },
  whatsapp_notifications:   { plan: 'addon_notifications', label: 'Advanced Notifications module' },
  whatsapp_automation:      { plan: 'addon_notifications', label: 'Advanced Notifications module' },
  notification_scheduling:  { plan: 'addon_notifications', label: 'Advanced Notifications module' },
  api_access:               { plan: 'addon_integrations', label: 'Custom Integrations module' },
  custom_integrations:      { plan: 'addon_integrations', label: 'Custom Integrations module' },
  accounting_sync:          { plan: 'addon_integrations', label: 'Custom Integrations module' },
  inventory_sync:           { plan: 'addon_integrations', label: 'Custom Integrations module' },
  webhooks:                 { plan: 'addon_integrations', label: 'Custom Integrations module' },
  webhook_support:          { plan: 'addon_integrations', label: 'Custom Integrations module' },

  // Remaining Kua Pro bundle features
  barcode_scanning:         { plan: 'kua_pro', label: 'Kua Pro' },
  loyalty_program:          { plan: 'kua_pro', label: 'Kua Pro' },
  custom_reporting:         { plan: 'kua_pro', label: 'Kua Pro' },
  priority_support:         { plan: 'kua_pro', label: 'Kua Pro' },
}

// ─── Singleton state (shared across all component instances) ─────────────────
const gates = ref<FeatureGates>({ features: {}, actions: {} })
const currentPlan = ref<PlanInfo | null>(null)
const loaded = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)
let loadingPromise: Promise<void> | null = null

// ─── Composable ──────────────────────────────────────────────────────────────
export function useFeatureGate(autoFetch = true) {

  async function fetchGates() {
    if (loaded.value) return
    if (loadingPromise) return loadingPromise

    loading.value = true
    loadingPromise = (async () => {
      error.value = null
      try {
        const { data } = await api.get('/features/access')
        if (data.success) {
          gates.value = data.feature_gates ?? { features: {}, actions: {} }
          currentPlan.value = data.subscription_status?.plan ?? null
          loaded.value = true
        }
      } catch (e: any) {
        // 402 = no subscription, still useful info
        if (e.response?.status === 402 || e.response?.status === 403) {
          gates.value = { features: {}, actions: {} }
          currentPlan.value = null
          loaded.value = true
        } else {
          error.value = 'Failed to load feature access'
        }
      } finally {
        loading.value = false
        loadingPromise = null
      }
    })()

    return loadingPromise
  }

  function resetGates() {
    gates.value = { features: {}, actions: {} }
    currentPlan.value = null
    loaded.value = false
    loading.value = false
    error.value = null
    loadingPromise = null
  }

  // Auto-fetch on first use if not already loaded
  if (autoFetch && !loaded.value && !loading.value) {
    fetchGates()
  }

  /** Check if a feature is available on the current plan */
  function canAccess(feature: string): boolean {
    return !!gates.value.features[feature]
  }

  /** Check if an action is allowed (product creation, user invite, etc.) */
  function canPerform(action: string): boolean {
    return !!gates.value.actions[action]
  }

  /** Check if a feature is locked (inverse of canAccess) */
  function isLocked(feature: string): boolean {
    // If gates haven't loaded yet, return true (locked) as fallback
    if (!loaded.value) {
      return true
    }
    return !canAccess(feature)
  }

  /** Get the minimum plan required for a feature */
  function requiredPlan(feature: string): string {
    return FEATURE_PLAN_MAP[feature]?.label ?? 'Kua Basic'
  }

  /** Get upgrade CTA text for a locked feature */
  function upgradeLabel(feature: string): string {
    const plan = requiredPlan(feature)
    return `Upgrade to ${plan}`
  }

  /** Computed: is the user on Anza plan */
  const isStorefrontStarter = computed(() => currentPlan.value?.code === 'storefront_starter')
  const isAnza = computed(() => currentPlan.value?.code === 'anza')
  const isKuaBasic = computed(() => currentPlan.value?.code === 'kua_basic')
  const isKuaPro = computed(() => currentPlan.value?.code === 'kua_pro')

  return {
    // State (readonly)
    gates: readonly(gates),
    currentPlan: readonly(currentPlan),
    loaded: readonly(loaded),
    loading: readonly(loading),
    error: readonly(error),

    // Plan checks
    isStorefrontStarter,
    isAnza,
    isKuaBasic,
    isKuaPro,

    // Methods
    fetchGates,
    resetGates,
    canAccess,
    canPerform,
    isLocked,
    requiredPlan,
    upgradeLabel,
  }
}
